'use client';

import Button from '@/components/Button';
import Input from '@/components/Form/FormInput';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { LeftArrowOutlined, QuestionOutlined, SearchOutlined } from '@zeelool/icon';
import { useRouter } from 'next/navigation';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import NoticeDialog from './NoticeDialog';
import { useTranslations } from 'next-intl';

// 搜索表单数据类型
interface SearchFormData {
  search_value: string;
}

// 搜索组件Props
interface SearchHeaderProps {
  onSearch: (searchValue: string) => void;
  loading?: boolean;
  className?: string;
}

const noBorderInputStyle = {
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
};

export default function SearchHeader({
  onSearch,
  loading = false,
  className = '',
}: SearchHeaderProps) {
  const t = useTranslations('track-order.header');
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const [showNoticeDialog, setShowNoticeDialog] = useState(false);
  const router = useRouter();

  // 表单管理
  const methods = useForm<SearchFormData>({
    defaultValues: {
      search_value: '',
    },
    mode: 'onSubmit',
  });

  const { control, handleSubmit } = methods;

  // 处理搜索提交
  const handleSearchSubmit = (data: SearchFormData) => {
    const trimmedValue = data.search_value.trim();
    if (trimmedValue) {
      onSearch(trimmedValue);
    }
  };

  // 处理回车键搜索
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(handleSearchSubmit)();
    }
  };

  return (
    <div
      className={`w-full bg-cover bg-center bg-no-repeat ${className} ${
        isWeb
          ? `bg-[url('/zeelool-next/track-order/track-order-bg.png')]`
          : `bg-[url('/zeelool-next/track-order/track-order-bg-mobile.png')]`
      }`}
    >
      <div className={`relative ${isWeb ? 'py-[40px]' : 'py-[60px]'}`}>
        {/* 标题和描述 */}
        <div className={`text-center ${isWeb ? 'mb-[48px]' : 'mb-[40px]'}`}>
          <h1
            className={`font-bb text-white mb-[16px] ${
              isWeb ? 'text-[48px] leading-[58px]' : 'text-[24px] leading-[29px]'
            }`}
          >
            {t('title')}
          </h1>
          <p
            className={`text-white font-br ${
              isWeb
                ? 'text-[14px] leading-[24px] max-w-[1080px] mx-auto'
                : 'text-[12px] leading-[20px] px-[14px]'
            }`}
          >
            {t('description')}
          </p>
        </div>

        {/* 搜索区域 */}
        <div className={`${isWeb ? 'max-w-[928px] mx-auto' : 'px-[14px]'}`}>
          <form onSubmit={handleSubmit(handleSearchSubmit)} className="w-full">
            <div
              className={`flex items-center gap-[12px] bg-white rounded-[21px] md:rounded-[8px] border border-gray-300 p-[4px] md:p-[8px]`}
            >
              {/* 搜索输入框 */}
              <div className="flex-1">
                <Input
                  name="search_value"
                  control={control}
                  placeholder={isWeb ? t('placeholderDesktop') : t('placeholderMobile')}
                  rules={{
                    required: t('requiredError'),
                    minLength: {
                      value: 1,
                      message: t('invalidError'),
                    },
                  }}
                  clearable
                  onKeyPress={handleKeyPress}
                  className={`${isWeb ? '!mb-0' : ''}`}
                  formItemClassName={`${isWeb ? '!mb-0' : ''}`}
                  slotProps={{
                    input: {
                      className: `${isWeb ? 'border-0 bg-transparent' : 'border-0 bg-white'}`,
                      startAdornment: <SearchOutlined className=" mr-[8px] text-[24px]" />,
                    },
                  }}
                  sx={{
                    '&.MuiTextField-root': {
                      margin: '0',
                      border: 'none',
                    },
                    '& .MuiOutlinedInput': {
                      border: 'none',
                    },
                    ...(isWeb
                      ? {}
                      : {
                          '& .MuiOutlinedInput-root': {
                            height: '24px',
                          },
                          '& .MuiInputBase-input': {
                            height: '24px',
                            padding: '0 4px',
                          },
                        }),
                    ...noBorderInputStyle,
                  }}
                  slotErrorText={(children) => <div className="mt-8">{children}</div>}
                />
              </div>

              {/* 搜索按钮 */}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                loading={loading}
                // disabled={!searchValue?.trim() || loading}
                className={`${
                  isWeb ? 'w-[120px] h-[48px] rounded-[6px]' : ' h-[32px] rounded-[16px]'
                }`}
                sx={{
                  '&.MuiButton-root': {
                    height: isWeb ? '46px' : '32px',
                    fontFamily: 'var(--font-bricolage-bold)',
                    borderRadius: isWeb ? '6px' : '26px',
                    fontSize: isWeb ? '16px' : '12px',
                  },
                }}
              >
                {t('search')}
              </Button>
            </div>
          </form>
        </div>

        {/* notion 区域 */}
        <div
          className="md:hidden cursor-pointer absolute top-[16px] left-[20px] text-white text-[14px]"
          onClick={() => router.back()}
        >
          <LeftArrowOutlined />
        </div>
        <div
          className="cursor-pointer flex items-center justify-center absolute top-[16px] right-[20px] md:right-[100px] lg:right-[300px]  2xl:right-[385px] text-white text-[14px]"
          onClick={() => {
            setShowNoticeDialog(true);
          }}
        >
          <QuestionOutlined
            size={24}
            className="text-white w-[16px] h-[16px] mr-[6px] cursor-pointer"
          />
          <span>{t('notice')}</span>
        </div>
      </div>

      <NoticeDialog open={showNoticeDialog} onClose={() => setShowNoticeDialog(false)} />
    </div>
  );
}
